/**
 * Weighted readiness score for a veteran's claim package. Surfaces on the
 * Command Center "Snapshot of my claim" panel. Pure functions — no React,
 * no API calls — so it can be reused inside PDFs, share links, or tests.
 *
 * Returns:
 *   {
 *     total: 0..100,
 *     band:  "red" | "amber" | "green",
 *     factors: [{ id, label, weight, score, status, detail }, ...]
 *   }
 *
 * Weights (sum = 100):
 *   profile        10
 *   conditions     10
 *   evidencePer    25  (per-condition: at least 1 evidence)
 *   statementsPer  25  (per-condition: at least 1 statement)
 *   appointments    5
 *   symptoms        5
 *   forms          20  (526EZ key fields)
 */

const WEIGHTS = {
  profile: 10,
  conditions: 10,
  evidencePer: 25,
  statementsPer: 25,
  appointments: 5,
  symptoms: 5,
  forms: 20,
};

function clamp01(n) {
  if (Number.isNaN(n) || !Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function pct(part, total) {
  if (!total) return 0;
  return clamp01(part / total);
}

export function computeReadinessSnapshot({
  profile = {},
  master526ez = {},
  issues = [],
  evidence = [],
  statements = [],
  appointments = [],
  symptoms = [],
}) {
  // --- Profile (name + branch) ---
  const profileFilled = !!(profile?.name && profile?.branch);
  const profileScore = profileFilled ? 1 : 0;

  // --- Conditions (≥1) ---
  const conditionsScore = issues.length > 0 ? 1 : 0;

  // --- Evidence per condition ---
  let evidenceScore = 0;
  if (issues.length > 0) {
    const evCovered = issues.filter((i) =>
      evidence.some((ev) => (ev.issueIds || []).includes(i.id))
    ).length;
    evidenceScore = pct(evCovered, issues.length);
  }

  // --- Statements per condition ---
  let statementsScore = 0;
  if (issues.length > 0) {
    const stCovered = issues.filter((i) =>
      statements.some((s) => s.issueId === i.id || s.linkedIssueId === i.id)
    ).length;
    statementsScore = pct(stCovered, issues.length);
  }

  // --- Appointments (≥1) ---
  const appointmentsScore = (appointments?.length || 0) > 0 ? 1 : 0;

  // --- Symptoms (≥7 distinct days logged) ---
  const symptomDays = new Set(
    (symptoms || [])
      .map((s) => (s.date || s.loggedAt || "").slice(0, 10))
      .filter(Boolean)
  );
  const symptomsScore = clamp01(symptomDays.size / 7);

  // --- Master Forms 526EZ key fields ---
  const formFields = [
    master526ez?.veteranName,
    master526ez?.dateOfBirth,
    master526ez?.claimType,
    master526ez?.serviceStart && master526ez?.serviceEnd,
    master526ez?.mailingAddress,
  ];
  const formsFilled = formFields.filter(Boolean).length;
  const formsScore = pct(formsFilled, formFields.length);

  // --- Weighted total ---
  const factors = [
    { id: "profile", label: "Veteran profile",
      desc: "Name + branch of service",
      weight: WEIGHTS.profile, score: profileScore,
      detail: profileFilled ? "Complete" : "Add your name + branch",
    },
    { id: "conditions", label: "Conditions added",
      desc: "At least one condition in the claim",
      weight: WEIGHTS.conditions, score: conditionsScore,
      detail: issues.length > 0 ? `${issues.length} condition${issues.length === 1 ? "" : "s"}` : "Add your first condition",
    },
    { id: "evidence", label: "Evidence per condition",
      desc: "Each condition has ≥1 piece of supporting evidence",
      weight: WEIGHTS.evidencePer, score: evidenceScore,
      detail: issues.length === 0
        ? "Add conditions first"
        : `${Math.round(evidenceScore * issues.length)} of ${issues.length} covered`,
    },
    { id: "statements", label: "Statements per condition",
      desc: "Each condition has ≥1 statement / nexus letter",
      weight: WEIGHTS.statementsPer, score: statementsScore,
      detail: issues.length === 0
        ? "Add conditions first"
        : `${Math.round(statementsScore * issues.length)} of ${issues.length} written`,
    },
    { id: "appointments", label: "Appointments logged",
      desc: "≥1 C&P / clinical visit on file",
      weight: WEIGHTS.appointments, score: appointmentsScore,
      detail: (appointments?.length || 0) > 0
        ? `${appointments.length} on file`
        : "Add an upcoming or past visit",
    },
    { id: "symptoms", label: "Symptom tracker",
      desc: "≥7 days of symptom data",
      weight: WEIGHTS.symptoms, score: symptomsScore,
      detail: `${symptomDays.size} day${symptomDays.size === 1 ? "" : "s"} logged`,
    },
    { id: "forms", label: "Master Forms 526EZ",
      desc: "Key fields completed (name, DOB, claim type, service dates, address)",
      weight: WEIGHTS.forms, score: formsScore,
      detail: `${formsFilled} of ${formFields.length} fields filled`,
    },
  ];

  factors.forEach((f) => {
    f.contributed = Math.round(f.score * f.weight * 100) / 100;
    if (f.score >= 0.999) f.status = "complete";
    else if (f.score > 0) f.status = "partial";
    else f.status = "empty";
  });

  const total = Math.round(factors.reduce((acc, f) => acc + f.contributed, 0));
  const band = total >= 75 ? "green" : total >= 40 ? "amber" : "red";

  return { total, band, factors };
}
